import React, { useContext } from 'react'

import { TicketsAppContext } from '../context/tickets-app-context'
import { PriceType } from '../../shared/components'
import {
  getMinimums,
  hitGeneralAdmissionMax,
  inManualUpdate,
  hitManualUpdateMax,
  hitEmployeeMax,
  totalTickets
} from '../helpers/logic'
import { STEPS } from '../helpers/steps'
import { OldCartContext } from '../context/old-cart-context'
import {
  addOnMaximum,
  ticketsWithConstituencyPricing,
  hitSpecificConstituencyMax
} from '../../shared/helpers/logic'

const getPrice = (step, priceType, key, member) => {
  let price = priceType.types[key].price
  if (step === STEPS.PARTY) {
    const originalPrice = priceType.types[key].price
    price = originalPrice
  }
  return price
}

function TicketsPriceType({
  priceType,
  last,
  priceKey = 'default',
  hitMaximumsByAge,
  noPrice,
  hidePricingColumn,
  remainingTickets,
  ...props
}) {
  const {
    actions: { updateItem },
    chosenEvent,
    membership,
    chosenAddOn,
    step,
    cart
  } = useContext(TicketsAppContext)

  const { manualMaximum } = useContext(OldCartContext)
  let addOnPrice = null
  const event = chosenAddOn ? chosenAddOn : chosenEvent
  // if an event has addOnPricing set to true we handle the pricing a little differently
  const isAddOn = event.addOnPricing
  // we need to get minimums here to make sure that the decrement button gets disabled
  // when a ticket has dependent tickets in the cart
  const minimums = getMinimums(chosenEvent, cart.items)
  // setting the price type key here
  const key =
    event.addOnPricing && !['chicago', 'illinois'].includes(priceKey)
      ? 'addon'
      : priceKey
  // getting the price object from the price type object based on the key above
  const price = priceType.types[key]
  // if there is no price we need to return null and add a warn to the console to
  // make debugging easier
  if (!price) {
    console.warn(
      `Error with priceType ${key} on ${event.name} with tess id ${event.id}`
    )
    return null
  }
  // grabbing the ticket from the built out tickets on the event (chosenEvent or chosenAddOn)
  const ticket = event.tickets.find(itemTicket => {
    if (price.id === itemTicket.priceTypeId) {
      return itemTicket
    }
  })

  // if we can't find a ticket on the event that matches our price type
  // we don't render the price since it cannot have quantity added to it
  // we add a warn to the console to help with debugging
  if (!ticket) {
    console.warn(
      `Error with priceType ${key} on ${event.name} with tess id ${event.id},
      no ticket was built that corresponds to this price`
    )
    return null
  }

  const stopIncrementConstTickets = () => {
    const employeeTickets = event.tickets.filter(
      t => t.pricingGroup == 'employee'
    )
    const constituencyTickets = ticketsWithConstituencyPricing(event.tickets)
    const constTickets = employeeTickets.concat(constituencyTickets)

    if (constTickets.length == 0) return false

    const objs = Object.groupBy(constTickets, ({ quantity }) =>
      quantity > 0 ? 'nonZero' : 'zero'
    )

    let specificConstituencyMax = []
    if (remainingTickets) {
      specificConstituencyMax = hitSpecificConstituencyMax(
        event,
        remainingTickets.remainingConstituencyTickets
      )
    }

    if (
      specificConstituencyMax.find(t => t.priceTypeId == ticket.priceTypeId) ||
      (objs.zero &&
        objs.nonZero &&
        objs.zero.find(t => t.id == ticket.id) &&
        objs.nonZero.length > 0) ||
      (objs.nonZero &&
        objs.nonZero.find(
          t => (t.pricingGroup == 'employee') == ticket.pricingGroup
        ) &&
        remainingTickets &&
        hitEmployeeMax(event, remainingTickets.remainingEmployeeTickets))
    ) {
      return true
    } else return false
  }

  if (isAddOn) {
    // checking if there are member pricing types on the priceType object
    // setting the key appropriately
    const member = Object.values(priceType.types).some(type => type.member)
      ? 'member'
      : 'nonMember'
    const price = getPrice(step, priceType, key, member)

    addOnPrice = {
      ...priceType.types[key],
      price
    }
  }
  // handle different cases where all increment buttons for a ticket
  // should be disabled
  let stopIncrement = false

  // this the "base" logic for disabling incrementation
  // modify stopIncrement with conditional expressions after this assignment
  stopIncrement = inManualUpdate
    ? hitManualUpdateMax(event, manualMaximum)
    : hitGeneralAdmissionMax(event)

  if (event.quantityLimit)
    stopIncrement = totalTickets(event.tickets) >= event.quantityLimit

  if (chosenAddOn) stopIncrement = addOnMaximum(chosenAddOn, [chosenEvent])
  // other conditions to disable increment buttons go above here

  // if children are not allowed on the membership we hide that ticketing option
  // from the user
  if (
    ticket.member &&
    ['child', 'youth'].includes(ticket.ageGroup) &&
    !membership.childrenAllowed
  ) {
    return null
  } else {
    return (
      <PriceType
        item={event}
        addOnPrice={addOnPrice}
        ticket={ticket}
        last={last}
        updateItem={updateItem}
        minimums={minimums}
        step={step}
        hitMaximumsByAge={hitMaximumsByAge}
        stopIncrement={stopIncrement || stopIncrementConstTickets()}
        noPrice={noPrice}
        hidePricingColumn={hidePricingColumn}
        {...props}
      />
    )
  }
}

export default TicketsPriceType
